<!-- frontend/src/views/Profile.vue -->
<template>
  <div class="profile-page">
    <div class="profile-container">
      <!-- 個人資訊卡片 -->
      <div class="profile-card">
        <div class="profile-header">
          <div class="avatar-section">
            <div class="avatar">
              <span class="avatar-text">{{ avatarText }}</span>
            </div>
            <div class="role-badge" :class="getRoleBadgeClass()">
              {{ getRoleDisplayName() }}
            </div>
          </div>
          <div class="info-section">
            <h1 class="user-name">{{ user.name }}</h1>
            <div class="user-details">
              <!-- 學生顯示 -->
              <template v-if="user.role === 'student'">
                <div class="detail-item">
                  <span class="detail-label">學號：</span>
                  <span class="detail-value">{{ user.user_id }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">系所：</span>
                  <span class="detail-value">{{ user.dept_name || getDepartmentName() }}</span>
                </div>
              </template>
              
              <!-- 部門/管理員顯示 -->
              <template v-else-if="user.role === 'admin'">
                <div class="detail-item">
                  <span class="detail-label">職員編號：</span>
                  <span class="detail-value">{{ user.user_id }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">部門：</span>
                  <span class="detail-value">{{ user.dept_name || getDepartmentName() }}</span>
                </div>
              </template>
              
              <!-- 教職員顯示 -->
              <template v-else-if="user.role === 'faculty' || user.role === 'staff' || user.role === 'facultyAndStaff'">
                <div class="detail-item">
                  <span class="detail-label">職員編號：</span>
                  <span class="detail-value">{{ user.user_id }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">部門：</span>
                  <span class="detail-value">{{ user.dept_name || getDepartmentName() }}</span>
                </div>
              </template>
              
              <div class="detail-item">
                <span class="detail-label">電子郵件：</span>
                <span class="detail-value">{{ user.email || '未提供' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 個人專區功能 -->
      <div class="features-section">
        <h2 class="section-title">個人專區</h2>
        
        <!-- 學生功能 -->
        <div v-if="user.role === 'student'" class="feature-grid">
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📚</div>
              <h3>學習歷程與成果追蹤</h3>
            </div>
            <p class="feature-desc">查看點數累積與學分兌換狀況</p>
            <router-link to="/my-activities?tab=history" class="feature-btn">
              前往查看
            </router-link>
          </div>
          
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📝</div>
              <h3>活動申請</h3>
            </div>
            <p class="feature-desc">申請參加個人權限範圍內的活動</p>
            <router-link to="/create-activity" class="feature-btn">
              申請活動
            </router-link>
          </div>
          
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">✍️</div>
              <h3>心得繳交</h3>
            </div>
            <p class="feature-desc">繳交活動心得與反思</p>
            <router-link to="/essay-submission" class="feature-btn">
              繳交心得
            </router-link>
          </div>
        </div>
        
        <!-- 部門/管理員功能 -->
        <div v-else-if="user.role === 'admin' || user.is_authorized === 1" class="feature-grid admin-grid">
          <!-- Admin 專屬：Email 設定 -->
          <div v-if="user.role === 'admin'" class="feature-card email-card">
            <div class="feature-header">
              <div class="feature-icon">📧</div>
              <h3>Email 通知設定</h3>
            </div>
            <p class="feature-desc">設定活動申請自動通知信功能</p>
            
            <div class="email-config">
              <div class="config-status">
                <span class="status-label">狀態：</span>
                <span :class="['status-badge', emailConfig.enabled ? 'enabled' : 'disabled']">
                  {{ emailConfig.enabled ? '已啟用' : '已停用' }}
                </span>
              </div>
              
              <button @click="showEmailConfig = true" class="config-btn">
                設定 Email
              </button>
            </div>
            
            <!-- Email 設定對話框 -->
            <div v-if="showEmailConfig" class="modal-overlay" @click.self="showEmailConfig = false">
              <div class="modal-content">
                <h3>Email 伺服器設定</h3>
                
                <div class="config-form">
                  <div class="form-group">
                    <label>SMTP 伺服器</label>
                    <input 
                      v-model="emailConfig.smtp_server" 
                      type="text" 
                      placeholder="例如：smtp.gmail.com"
                      class="config-input"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label>SMTP 連接埠</label>
                    <input 
                      v-model="emailConfig.smtp_port" 
                      type="number" 
                      placeholder="例如：587"
                      class="config-input"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label>寄件者 Email</label>
                    <input 
                      v-model="emailConfig.sender_email" 
                      type="email" 
                      placeholder="noreply@nchu.edu.tw"
                      class="config-input"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label>寄件者密碼</label>
                    <input 
                      v-model="emailConfig.sender_password" 
                      type="password" 
                      placeholder="輸入密碼"
                      class="config-input"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input 
                        v-model="emailConfig.use_tls" 
                        type="checkbox"
                      >
                      <span>使用 TLS 加密</span>
                    </label>
                  </div>
                  
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input 
                        v-model="emailConfig.enabled" 
                        type="checkbox"
                      >
                      <span>啟用自動通知</span>
                    </label>
                  </div>
                </div>
                
                <div class="modal-actions">
                  <button @click="testEmailConfig" class="test-btn">
                    測試連線
                  </button>
                  <button @click="saveEmailConfig" class="save-btn">
                    儲存設定
                  </button>
                  <button @click="showEmailConfig = false" class="cancel-btn">
                    取消
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Admin 專屬：權限管理 -->
          <div v-if="user.role === 'admin'" class="feature-card permission-card">
            <div class="feature-header">
              <div class="feature-icon">👑</div>
              <h3>權限管理</h3>
            </div>
            <p class="feature-desc">賦予或撤銷使用者管理權限</p>
            
            <!-- 新增權限 -->
            <div class="permission-form">
              <h4>賦予管理權限</h4>
              <div class="input-group">
                <input 
                  type="text" 
                  v-model="newPermissionUser" 
                  placeholder="輸入使用者帳號"
                  class="permission-input"
                >
                <button @click="grantPermission" class="add-btn">
                  賦予權限
                </button>
              </div>
            </div>
            
            <!-- 權限名單 -->
            <div class="permission-list">
              <h4>管理員名單</h4>
              <div v-if="permissionList.length === 0" class="empty-list">
                尚無其他管理員
              </div>
              <div v-else class="user-list">
                <div v-for="user in permissionList" :key="user.user_id" class="user-item">
                  <span class="user-info">
                    <span class="user-name">{{ user.name || user.user_id }}</span>
                    <span class="user-account">({{ user.user_id }})</span>
                  </span>
                  <button @click="revokePermission(user.user_id)" class="remove-btn">
                    撤銷
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="feature-card admin-card">
            <div class="feature-header">
              <div class="feature-icon">⚙️</div>
              <h3>管理介面</h3>
            </div>
            <p class="feature-desc">系統管理與設定</p>
            <div class="admin-actions">
              <button @click="goToAdminPanel" class="feature-btn primary">
                進入管理介面
              </button>
            </div>
          </div>
          
          <div v-if="user.role === 'admin'" class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📅</div>
              <h3>活動管理</h3>
            </div>
            <p class="feature-desc">發布、編輯、審核活動</p>
            <div class="feature-stats">
              <div class="stat-item">
                <span class="stat-value">{{ pendingActivities }}</span>
                <span class="stat-label">待審核</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ activeActivities }}</span>
                <span class="stat-label">進行中</span>
              </div>
            </div>
            <router-link to="/activity-approval" class="feature-btn">
              管理活動
            </router-link>
          </div>
          
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">👥</div>
              <h3>學生歷程審核</h3>
            </div>
            <p class="feature-desc">審核學生心得與學分轉換申請</p>
            <div class="feature-stats">
              <div class="stat-item">
                <span class="stat-value">{{ pendingEssays }}</span>
                <span class="stat-label">待審心得</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ pendingCredits }}</span>
                <span class="stat-label">待審學分</span>
              </div>
            </div>
            <button @click="goToReview" class="feature-btn">
              開始審核
            </button>
          </div>
          
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📊</div>
              <h3>數據報表</h3>
            </div>
            <p class="feature-desc">查看學生活動參與數據與分析</p>
            <button @click="goToReports" class="feature-btn">
              查看報表
            </button>
          </div>
          
          <div v-if="user.role === 'admin'" class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">🎯</div>
              <h3>活動管理</h3>
            </div>
            <p class="feature-desc">管理活動報名與參與者名單</p>
            <router-link to="/registration-management" class="feature-btn">
              管理報名
            </router-link>
          </div>
        </div>
        
        <!-- 教職員功能 (facultyAndStaff 只有學生權限) -->
        <div v-else-if="user.role === 'faculty' || user.role === 'staff' || user.role === 'facultyAndStaff'" class="feature-grid">
          <!-- 教職員 faculty 有管理權限 -->
          <template v-if="user.role === 'faculty'">
            <div class="feature-card">
              <div class="feature-header">
                <div class="feature-icon">📅</div>
                <h3>活動管理</h3>
              </div>
              <p class="feature-desc">發布、編輯、審核活動</p>
              <router-link to="/activity-approval" class="feature-btn">
                管理活動
              </router-link>
            </div>
            
            <div class="feature-card">
              <div class="feature-header">
                <div class="feature-icon">🎯</div>
                <h3>活動管理</h3>
              </div>
              <p class="feature-desc">管理活動報名與參與者名單</p>
              <router-link to="/registration-management" class="feature-btn">
                管理報名
              </router-link>
            </div>
          </template>
          
          <!-- facultyAndStaff 只有基本功能 -->
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📚</div>
              <h3>學習歷程</h3>
            </div>
            <p class="feature-desc">查看個人參與活動紀錄</p>
            <router-link to="/my-activities?tab=history" class="feature-btn">
              查看歷程
            </router-link>
          </div>
          
          <div class="feature-card">
            <div class="feature-header">
              <div class="feature-icon">📝</div>
              <h3>活動報名</h3>
            </div>
            <p class="feature-desc">報名參加各類研習活動</p>
            <router-link to="/search" class="feature-btn">
              瀏覽活動
            </router-link>
          </div>
        </div>
      </div>

      <!-- 登出按鈕 -->
      <div class="logout-section">
        <button @click="logout" class="logout-btn">
          <span class="logout-icon">🚪</span>
          登出系統
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getPermissionList, 
  grantUserPermission, 
  revokeUserPermission, 
  searchUserByAccount,
  getEmailConfig,
  updateEmailConfig,
  testEmailConfig
} from '@/api.js'

export default {
  name: 'Profile',
  data() {
    return {
      user: {
        user_id: '',
        name: '',
        role: '',
        is_authorized: 0,
        dept_id: null,
        dept_name: '',
        email: ''
      },
      // 管理員統計資料（模擬）
      pendingActivities: 5,
      activeActivities: 12,
      pendingEssays: 8,
      pendingCredits: 3,
      lastLoginTime: new Date().toLocaleString('zh-TW'),
      // 權限管理
      newPermissionUser: '',
      permissionList: [],
      // Email 設定
      showEmailConfig: false,
      emailConfig: {
        smtp_server: '',
        smtp_port: 587,
        sender_email: '',
        sender_password: '',
        use_tls: true,
        enabled: false
      }
    }
  },
  computed: {
    avatarText() {
      return this.user.name ? this.user.name.charAt(0) : '?'
    }
  },
  created() {
    this.loadUserProfile()
    if (this.user.role === 'admin') {
      this.loadPermissionList()
      this.loadEmailConfig()
    }
  },
  watch: {
    'user.role'(newRole) {
      if (newRole === 'admin') {
        this.loadPermissionList()
        this.loadEmailConfig()
      }
    }
  },
  methods: {
    loadUserProfile() {
      const userInfo = localStorage.getItem('user_info')
      if (userInfo) {
        const parsed = JSON.parse(userInfo)
        this.user = {
          user_id: parsed.user_id || '',
          name: parsed.name || '',
          role: parsed.role || '',
          is_authorized: parsed.is_authorized || 0,
          dept_id: parsed.dept_id || null,
          dept_name: parsed.dept_name || '',
          email: parsed.email || ''
        }
      } else {
        this.$router.push('/login')
      }
    },
    
    getRoleDisplayName() {
      const roleMap = {
        'student': '學生',
        'dept': '部門管理員',
        'admin': '系統管理員',
        'faculty': '教師',
        'staff': '職員',
        'facultyAndStaff': '教職員'
      }
      return roleMap[this.user.role] || '使用者'
    },
    
    getRoleBadgeClass() {
      return `role-${this.user.role}`
    },
    
    getDepartmentName() {
      // 根據 dept_id 返回部門名稱
      const deptMap = {
        1: '通識教育中心',
        2: '資訊工程學系',
        3: '電機工程學系',
        4: '管理學院'
      }
      return deptMap[this.user.dept_id] || '未設定'
    },
    
    getRolePermissions() {
      const permMap = {
        'student': '活動報名、心得繳交、學分兌換',
        'dept': '活動管理、審核權限、數據查看',
        'admin': '完整系統管理權限',
        'faculty': '活動報名、研習參與',
        'staff': '活動報名、行政作業'
      }
      return permMap[this.user.role] || '基本權限'
    },
    
    goToAdminPanel() {
      // 導向管理介面
      alert('管理介面功能開發中...')
    },
    
    goToReview() {
      // 導向審核頁面
      this.$router.push('/essay-review')
    },
    
    goToReports() {
      // 導向學習歷程與成果追蹤（我的活動）
      this.$router.push('/my-activities?tab=history')
    },
    
    logout() {
      if (confirm('確定要登出系統嗎？')) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('token_type')
        localStorage.removeItem('user_info')
        this.$router.push('/login')
      }
    },
    
    // 權限管理功能 (Admin only)
    async loadPermissionList() {
      try {
        const response = await getPermissionList()
        if (response && response.data) {
          // 過濾掉自己和其他 admin（只顯示可管理的使用者）
          this.permissionList = response.data.filter(user =>
            user.user_id !== this.user.user_id && user.role !== 'admin'
          )
        }
      } catch (error) {
        console.error('載入權限名單失敗:', error)
        this.permissionList = []
      }
    },
    
    async grantPermission() {
      if (!this.newPermissionUser) {
        alert('請輸入使用者帳號')
        return
      }
      
      try {
        // 先搜尋使用者確認存在
        const searchResponse = await searchUserByAccount(this.newPermissionUser)
        if (!searchResponse || !searchResponse.data) {
          alert('找不到該使用者')
          return
        }
        
        const user = searchResponse.data
        
        // 檢查是否已有權限
        if (user.role === 'admin' || user.is_authorized === 1) {
          alert('此使用者已有管理權限')
          return
        }
        
        // 賦予權限
        const response = await grantUserPermission(this.newPermissionUser)
        
        if (response && response.data) {
          alert(response.data.message)
          
          // 重新載入權限名單
          await this.loadPermissionList()
          this.newPermissionUser = ''
        }
      } catch (error) {
        console.error('賦予權限失敗:', error)
        const errorMsg = error.response?.data?.detail || '賦予權限失敗'
        alert(errorMsg)
      }
    },
    
    async revokePermission(userId) {
      const user = this.permissionList.find(u => u.user_id === userId)
      if (!user) return

      if (!confirm(`確定要撤銷 ${user.name || user.user_id} 的管理權限嗎？`)) {
        return
      }
      
      try {
        const response = await revokeUserPermission(userId)
        
        if (response && response.data) {
          alert(response.data.message)
          
          // 重新載入權限名單
          await this.loadPermissionList()
        }
      } catch (error) {
        console.error('撤銷權限失敗:', error)
        const errorMsg = error.response?.data?.detail || '撤銷權限失敗'
        alert(errorMsg)
      }
    },
    
    savePermissionList() {
      // 已改為使用 API，此函式不再需要
      console.log('Permission list is now saved via API')
    },
    
    // Email 設定功能 (Admin only)
    async loadEmailConfig() {
      try {
        const response = await getEmailConfig()
        if (response && response.data) {
          this.emailConfig = response.data
        }
      } catch (error) {
        console.error('載入 Email 設定失敗:', error)
      }
    },
    
    async saveEmailConfig() {
      try {
        const response = await updateEmailConfig(this.emailConfig)
        if (response && response.data) {
          alert('Email 設定已儲存')
          this.showEmailConfig = false
          await this.loadEmailConfig()
        }
      } catch (error) {
        console.error('儲存 Email 設定失敗:', error)
        alert('儲存失敗，請檢查設定')
      }
    },
    
    async testEmailConfig() {
      try {
        const response = await testEmailConfig()
        if (response && response.data) {
          alert(response.data.message)
        }
      } catch (error) {
        console.error('測試 Email 失敗:', error)
        const errorMsg = error.response?.data?.detail || '測試失敗，請檢查設定'
        alert(errorMsg)
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.profile-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 個人資訊卡片 */
.profile-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 30px;
}

.avatar-section {
  position: relative;
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0,123,255,0.3);
}

.avatar-text {
  font-size: 36px;
  color: white;
  font-weight: bold;
}

.role-badge {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.role-student {
  background: #e3f2fd;
  color: #1976d2;
}

.role-dept {
  background: #f3e5f5;
  color: #7b1fa2;
}

.role-admin {
  background: #ffebee;
  color: #c62828;
}

.role-faculty {
  background: #e8f5e9;
  color: #388e3c;
}

.role-staff {
  background: #fff3e0;
  color: #f57c00;
}

.info-section {
  flex: 1;
}

.user-name {
  margin: 0 0 15px 0;
  font-size: 32px;
  color: #333;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-label {
  color: #666;
  font-weight: 500;
  min-width: 80px;
}

.detail-value {
  color: #333;
  font-weight: 400;
}

/* 功能區 */
.features-section {
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 24px;
  color: #333;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.admin-grid {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.feature-card {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 12px;
  transition: all 0.3s;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.admin-card {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.feature-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.feature-icon {
  font-size: 28px;
}

.feature-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.admin-card .feature-header h3 {
  color: white;
}

.feature-desc {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.admin-card .feature-desc {
  color: rgba(255,255,255,0.9);
}

.feature-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

.feature-btn {
  display: inline-block;
  padding: 10px 20px;
  background: #007bff;
  color: white;
  text-decoration: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.feature-btn:hover {
  background: #0056b3;
  transform: translateX(5px);
}

.feature-btn.primary {
  background: white;
  color: #667eea;
}

.feature-btn.primary:hover {
  background: #f8f9fa;
}

/* 權限管理卡片 */
.permission-card {
  background: linear-gradient(135deg, #ffd700, #ffb300);
  color: #333;
  grid-column: span 2;
}

.permission-card .feature-header h3 {
  color: #333;
}

.permission-card .feature-desc {
  color: #555;
  margin-bottom: 25px;
}

.permission-form {
  margin-bottom: 25px;
}

.permission-form h4 {
  color: #333;
  font-size: 16px;
  margin-bottom: 15px;
}

.input-group {
  display: flex;
  gap: 10px;
}

.permission-input {
  flex: 1;
  padding: 10px 15px;
  border: 2px solid rgba(255,255,255,0.5);
  border-radius: 6px;
  background: rgba(255,255,255,0.9);
  font-size: 14px;
}

.permission-input:focus {
  outline: none;
  border-color: #ffb300;
  background: white;
}

.add-btn {
  padding: 10px 20px;
  background: #333;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
}

.add-btn:hover {
  background: #555;
  transform: translateY(-2px);
}

.permission-list {
  background: rgba(255,255,255,0.95);
  padding: 20px;
  border-radius: 8px;
}

.permission-list h4 {
  color: #333;
  font-size: 16px;
  margin-bottom: 15px;
}

.empty-list {
  color: #999;
  text-align: center;
  padding: 20px;
  font-style: italic;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s;
}

.user-item:hover {
  border-color: #ffb300;
  box-shadow: 0 2px 8px rgba(255,183,0,0.2);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-name {
  font-weight: 500;
  color: #333;
}

.user-account {
  color: #666;
  font-size: 14px;
}

.remove-btn {
  padding: 6px 15px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
}

.remove-btn:hover {
  background: #c82333;
}

/* 其他樣式調整 */
.logout-section {
  text-align: center;
  margin-top: 30px;
}

.logout-btn {
  padding: 15px 40px;
  background: white;
  color: #dc3545;
  border: 2px solid #dc3545;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.logout-btn:hover {
  background: #dc3545;
  color: white;
}

.logout-icon {
  font-size: 20px;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    text-align: center;
  }
  
  .nav-grid,
  .feature-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .feature-stats {
    justify-content: space-around;
  }
}
</style>